import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ScanLine, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { api, fmtCHF } from "@/lib/api";
import { prepareImage } from "@/lib/image";
import { DOC_STATUS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";

export default function ScanBulk() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]); // {name, status: 'waiting'|'processing'|docstatus, doc}
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const start = async (files) => {
    const list = Array.from(files).slice(0, 30);
    if (!list.length) return;
    setRunning(true);
    setDone(false);
    setItems(list.map((f) => ({ name: f.name, status: "processing" })));

    // 1) Acquisizione immediata di tutti i file (originale persistito subito)
    const docIds = [];
    for (let i = 0; i < list.length; i++) {
      try {
        const prepared = await prepareImage(list[i]);
        const { data } = await api.post("/scan", {
          image_base64: prepared.dataUrl.split(",")[1],
          filename: prepared.filename,
          channel: "bulk",
          client_preprocessed: prepared.preprocessed,
        });
        docIds[i] = data.document.doc_id;
      } catch {
        docIds[i] = null;
        setItems((prev) => prev.map((it, j) => (j === i ? { ...it, status: "to_confirm", doc: { note: "Acquisizione non riuscita" } } : it)));
      }
    }

    // 2) L'elaborazione prosegue server-side: qui osserviamo solo lo stato
    for (let round = 0; round < 45; round++) {
      await new Promise((r) => setTimeout(r, 3000));
      let pending = 0;
      for (let i = 0; i < list.length; i++) {
        if (!docIds[i]) continue;
        try {
          const { data: d } = await api.get(`/documents/${docIds[i]}`);
          if (d.status === "processing") { pending++; continue; }
          setItems((prev) => prev.map((it, j) => (j === i && it.status === "processing" ? { ...it, status: d.status, doc: d } : it)));
        } catch { pending++; }
      }
      if (pending === 0) break;
    }
    setRunning(false);
    setDone(true);
  };

  const count = (s) => items.filter((i) => i.status === s).length;
  const finished = items.filter((i) => ["ready", "to_confirm", "anomaly"].includes(i.status)).length;

  return (
    <div className="max-w-2xl mx-auto px-4" data-testid="bulk-page">
      <Link to="/scan" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Scansiona</Link>
      <div className="mb-6 fade-up">
        <p className="text-xs tracking-[0.25em] text-gray-500">OCCHI & ORECCHIE · ACQUISIZIONE</p>
        <h1 className="font-display text-3xl font-bold mt-1">Bulk Scan</h1>
        <p className="text-sm text-gray-500 mt-1">Fotografa i documenti accumulati durante la settimana. Il sistema li separa, li legge e ti segnala solo le eccezioni.</p>
      </div>

      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        data-testid="bulk-input" onChange={(e) => start(e.target.files)} />

      {items.length === 0 && (
        <button data-testid="bulk-start-btn" onClick={() => inputRef.current.click()}
          className="w-full glass rounded-3xl p-10 flex flex-col items-center gap-3 scan-glow hover:border-cyan-400/40 transition-colors fade-up">
          <span className="w-16 h-16 rounded-full flex items-center justify-center text-[#030712]"
            style={{ background: "linear-gradient(135deg,#00E5FF,#3B82F6)" }}>
            <ScanLine size={28} />
          </span>
          <span className="font-display font-bold text-xl">AVVIA BULK SCAN</span>
          <span className="text-xs text-gray-500">Seleziona fino a 30 foto di scontrini e fatture</span>
        </button>
      )}

      {items.length > 0 && (
        <div className="space-y-4 fade-up">
          <div className="glass rounded-3xl p-6" data-testid="bulk-summary">
            <SectionTitle>Riepilogo elaborazione</SectionTitle>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div><div className="font-display text-2xl font-bold" data-testid="bulk-count-total">{items.length}</div><div className="text-[10px] text-gray-500 uppercase tracking-wider">Acquisiti</div></div>
              <div><div className="font-display text-2xl font-bold text-emerald-400" data-testid="bulk-count-ready">{count("ready")}</div><div className="text-[10px] text-gray-500 uppercase tracking-wider">Pronti</div></div>
              <div><div className="font-display text-2xl font-bold text-amber-400" data-testid="bulk-count-confirm">{count("to_confirm")}</div><div className="text-[10px] text-gray-500 uppercase tracking-wider">Da confermare</div></div>
              <div><div className="font-display text-2xl font-bold text-red-400" data-testid="bulk-count-anomaly">{count("anomaly")}</div><div className="text-[10px] text-gray-500 uppercase tracking-wider">Anomalie</div></div>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(finished / items.length) * 100}%`, background: "linear-gradient(90deg,#3B82F6,#00E5FF)" }} />
            </div>
            {done && (
              <p className="text-xs text-gray-400 mt-3" data-testid="bulk-done-message">
                Elaborazione completata. Ti segnaliamo solo {count("to_confirm") + count("anomaly")} eccezioni: il resto è già pronto.
              </p>
            )}
          </div>

          <div className="space-y-2" data-testid="bulk-list">
            {items.map((it, i) => {
              const st = DOC_STATUS[it.status];
              return (
                <div key={i} data-testid={`bulk-item-${i}`} className="glass rounded-2xl px-4 py-3 flex items-center gap-3">
                  {it.status === "processing" && <Clock size={15} className="text-cyan-300 animate-pulse shrink-0" />}
                  {it.status === "waiting" && <Clock size={15} className="text-gray-600 shrink-0" />}
                  {it.status === "ready" && <CheckCircle2 size={15} className="text-emerald-400 shrink-0" />}
                  {it.status === "to_confirm" && <AlertTriangle size={15} className="text-amber-400 shrink-0" />}
                  {it.status === "anomaly" && <AlertTriangle size={15} className="text-red-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-200 truncate">{it.doc?.supplier || it.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {it.doc ? `${it.doc.doc_type}${it.doc.amount != null ? " · " + fmtCHF(it.doc.amount) : ""}${it.doc.note ? " · " + it.doc.note : ""}` : it.status === "processing" ? "Il sistema sta leggendo…" : "In attesa"}
                    </div>
                  </div>
                  {st && <span className="text-[10px] font-medium" style={{ color: st.color }}>{st.label}</span>}
                </div>
              );
            })}
          </div>

          {done && (
            <Link to="/queue" data-testid="bulk-goto-queue" className="btn-primary w-full !py-3.5 flex">
              Vai alla coda di elaborazione
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
